import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaHistory } from 'react-icons/fa';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { 
  Container,
  Content,
  TabContainer,
  TabButton,
  FormContainer,
  FormSection,
  SectionTitle,
  FormRow,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  HeaderButton,
} from './styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/en-gb';
import { employeesApi, organizationApi, projectsApi } from '../../../services/api';
import { hire } from '../../../services/event-handlers/employment/hire';
import { rehire as rehireEmployee } from '../../../services/event-handlers/employment/hire/rehire';
import { replaceContract, scheduleReplacement } from '../../../services/event-handlers/employment/resign/contractSchedule';
import { isRadarSourced, personVerbsLockedBy, sourceLockNote } from '../../../constants/employmentSourceSystems';
import { useAuth } from '../../../contexts/AuthContext';
import { formatTimeDuration } from '../../../utils/dateUtils';
import { useAlert } from '../../../contexts/AlertContext';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import ManagementHistoryDialog from './components/ManagementHistoryDialog';
import LoadingSpinner from '../../common/LoadingSpinner';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { eventDayFmt, renewalSwitchDay } from '../../../constants/employmentEventAnnotations';

dayjs.extend(utc);
import { getStatusColor, getStatusTextColor, getStatusText } from '../../../constants/organizationalStatuses';
import { ORGANIZATIONAL_STATUSES } from '../../../constants/organizationalStatuses';
// ADR-149 §5.2 — the person action area. Resign is hosted by the same shared
// router the Contracts tab uses (ADR-113 §5's one implementation per verb),
// mounted here with NO episode: the verb is about the person.
import { PERSON_ACTIONS, contractIdentityLabel } from '../../../constants/contractActions';
import ContractActionModals from '../components/ContractActionModals';

// ADR-149 §5.2 / ADR-158 §7.2 — the person action area's destructive verbs
// share one outlined-red treatment: every one of them ends or voids the
// person's employment, and none is the house primary.
const PERSON_DESTRUCTIVE_SX = {
  textTransform: 'none',
  fontSize: '14px',
  height: '38px',
  color: '#B91C1C',
  borderColor: '#FCA5A5',
  '&:hover': { borderColor: '#B91C1C', backgroundColor: '#FEF2F2' }
};
import { useEmployeePermissions } from '../../../hooks/useEmployeePermissions';
import { RESIGN_REASONS, RESIGN_REASON_OPTIONS } from '../../../constants/resignReasons';
import Competencies from './components/Competencies';
import EmptyState from '../../common/EmptyState';
import Assignments from './components/Assignments';
import InstantFeedback from './components/InstantFeedback';
import EmploymentEpisodes from './components/EmploymentEpisodes';
// ADR-138 — the hire date is taken from an employment episode, so it always matches
// a real period of employment. Same picker on both surfaces (rehire, and the action
// beside the Hire Date field) so the choice is never described two different ways.
import EpisodePicker from './components/EpisodePicker';
import {
  HIRE_DATE_SOURCE_KIND,
  formatEpisodeDate,
  toEpisodeDayKey,
} from '../../../constants/employmentEpisodes';
import Responsibilities from './components/Responsibilities';
import PlacementActivity from './components/PlacementActivity';
import { useAbility } from '../../../contexts/AbilityContext';
import { resolveIndividualOpenMode, buildResolvedIndividualNavigation } from '../../../utils/individualOpenMode';
import { INDIVIDUAL_OPEN_MODES, INDIVIDUAL_PAGE_MODES } from '../../../constants/individualOpenModes';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { ROLE_TYPES } from '../../../constants/roleTypes';
import useDebounce from '../../../hooks/useDebounce';
import ClusterSelector from '../../common/ClusterSelector';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../constants/orgStructureTypes';
import { contractCardText } from '../../../utils/contractCard';
import {
  resolveReplaceTarget, replaceTargetFacts, replaceTargetBounds, replaceSuccessorProblem, replaceGapWarning
} from '../../../utils/replaceTarget';
import CountrySelector from '../../common/CountrySelector';
import ContractDetails from './components/ContractDetails';
import PositionSelector from '../../common/PositionSelector';
import GradeSelector from '../../common/GradeSelector';
// ADR-173 — the shared contract-term fields and fixed-term block
import {
  ContractTypeField, ContractCompanyField, EngagementModelField, ContractNumberField
} from '../components/ContractTermsFields';
import FixedTermFields from '../components/FixedTermFields';
import ProjectCard from '../components/ProjectCard';

const TABS = {
  EMPLOYMENT_INFORMATION: 'EMPLOYMENT_INFORMATION',
  // ADR-142 §11 — current-state view of the contract(s); read-only always
  CONTRACTS: 'CONTRACTS',
  PROJECTS: 'PROJECTS',
  RESPONSIBILITIES: 'RESPONSIBILITIES',
  COMPETENCIES: 'COMPETENCIES',
  ASSIGNMENTS: 'ASSIGNMENTS',
  INSTANT_FEEDBACK: 'INSTANT_FEEDBACK',
  EMPLOYMENT_EPISODES: 'EMPLOYMENT_EPISODES',
  PLACEMENT_ACTIVITY: 'PLACEMENT_ACTIVITY',
};

const EMPLOYEE_ACTIONS = {
  REHIRE: 'rehire',
  // ADR-136 phase 5 — Replace Contract: the §5a supersession (cancel the running
  // contract at a today/past cutover + start the successor, one transaction).
  REPLACE: 'replace',
};

const EmployeeForm = ({ onBack, scrollState, onEmployeeUpdate, requestedMode = INDIVIDUAL_PAGE_MODES.EDIT }) => {
  const navigate = useNavigate();
  const { id, mode } = useParams();
  const location = useLocation();
  const { showAlert } = useAlert();
  const ability = useAbility();
  // ADR-136 phase 5: replace mode IS rehire mode plus the cutover — it inherits
  // every rehire behavior (blank contract fields, hire-date source panel,
  // contract number, fixed-term section) and adds the cancel leg on top.
  const isReplaceMode = mode === EMPLOYEE_ACTIONS.REPLACE;
  const isRehireMode = mode === EMPLOYEE_ACTIONS.REHIRE || isReplaceMode;
  const { canManageEmployee } = useEmployeePermissions();
  // ADR-183 — only an admin may change a sync-owned contract
  const { user: authUser } = useAuth();
  const viewerIsAdmin = Boolean(authUser?.employee?.isAdmin);
  // ADR-109 §1 — `auto` (/employees/:id) is read-only until fetchEmployee resolves the
  // mode and replaces the URL with /edit or /view. Rehire/replace live under
  // /edit/:mode, so they always arrive with requestedMode EDIT.
  const isAutoMode = requestedMode === INDIVIDUAL_OPEN_MODES.AUTO;
  const isViewMode = requestedMode !== INDIVIDUAL_PAGE_MODES.EDIT;
  // ADR-142 §5.4 / ADR-143 — the term SELECTORS exist only where terms are
  // settable: create and rehire (the verbs that start a contract). On an
  // existing person the terms are contract facts shown on the Contracts tab
  // and changed by amendment; rendering them here as permanently-disabled
  // dropdowns misread as a permissions problem. A read-only strip replaces
  // them (below).
  const showTermSelectors = !id || isRehireMode;
  // ADR-019 — hire date is disabled in edit mode unless the user holds this permission.
  const canEditHireDate = ability.can(ACTIONS.UPDATE, SUBJECTS.EMPLOYEE_HIRE_DATE);

  // ADR-138 — a strictly narrower right: adopt an existing episode's start date as the
  // hire date. It cannot write an arbitrary value, which is why it is grantable to
  // people who must not hold the free-text edit above.
  const canSetHireDateFromEpisode = ability.can(ACTIONS.UPDATE, SUBJECTS.EMPLOYEE_HIRE_DATE_FROM_EPISODE);
  const [episodePickerOpen, setEpisodePickerOpen] = useState(false);
  const [isApplyingHireDate, setIsApplyingHireDate] = useState(false);
  // ADR-138 — the employee's STORED hire/resign dates. Kept outside formData because
  // rehire mode deliberately blanks those fields, and the picker needs to show what
  // the record says today so the operator can decide whether to keep it.
  const [originalHireDate, setOriginalHireDate] = useState(null);
  const [originalResignDate, setOriginalResignDate] = useState(null);
  // ADR-138 rehire — { kind, episodeId, date }. `kind: 'keep'` sends nothing at all,
  // keeping that path byte-for-byte the pre-ADR-138 rehire.
  const [hireDateSource, setHireDateSource] = useState({
    kind: HIRE_DATE_SOURCE_KIND.KEEP,
    episodeId: null,
    date: null,
  });
  const [hireDateSourceTouched, setHireDateSourceTouched] = useState(false);
  // ADR-136 phase 4: once the operator edits the fixed-term comment it is theirs —
  // auto-generation stops; clearing the end date re-arms it. ADR-173 D2: that
  // touched flag now lives in the shared FixedTermFields.
  // ADR-136 phase 5 — Replace Contract state. Cutover defaults to today (the
  // verb's envelope is today/past only); the successor start and keep-projects
  // defaults derive from it until the operator overrides.
  const [replaceCutoverDate, setReplaceCutoverDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [replaceReason, setReplaceReason] = useState('');
  const [keepProjects, setKeepProjects] = useState(true);
  const [keepProjectsTouched, setKeepProjectsTouched] = useState(false);
  const [replaceStartTouched, setReplaceStartTouched] = useState(false);
  // The OLD contract's start — the cutover cannot precede it.
  const [originalContractStartDate, setOriginalContractStartDate] = useState(null);
  // ADR-136 phase 6 (§15 slice 3): who owns the running contract — scheduling a
  // replacement on an INGESTION-owned one warns (never blocks, decision 4); and
  // an already-scheduled replacement's cutover — the backend rejects a second
  // pending renewal, so the form says so up front.
  const [currentEpisodeSource, setCurrentEpisodeSource] = useState(null);
  const [pendingRenewalCutover, setPendingRenewalCutover] = useState(null);
  // With concurrent contracts the replace flow addresses ONE contract: the one
  // named by ?contract= (or the launcher's router state), else the primary.
  // Every replace guard and default above is read from that contract's
  // contracts[] entry, never from the person view (which is the primary's).
  const requestedReplaceEpisodeId = isReplaceMode
    ? (new URLSearchParams(location.search).get('contract') || location.state?.replaceEpisodeId || null)
    : null;
  const [replaceTargetEpisodeId, setReplaceTargetEpisodeId] = useState(null);
  const [replaceTargetLabel, setReplaceTargetLabel] = useState('');
  // ADR-188 D4/D5 — a secondary's replacement: bounded by the primary's end,
  // and it changes no person fact
  const [replaceBounds, setReplaceBounds] = useState({ isSecondary: false, ownEndYmd: null, primaryEndYmd: null });
  const isSecondaryReplace = isReplaceMode && replaceBounds.isSecondary;

  // ADR-101 — management info history affordance (as-of viewer, timeline in phase 2)
  const canViewManagementHistory = ability.can(ACTIONS.READ, SUBJECTS.MANAGEMENT_HISTORY);
  const [managementHistoryOpen, setManagementHistoryOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(id ? true : false);
  const [activeTab, setActiveTab] = useState(TABS.EMPLOYMENT_INFORMATION);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    position: '',
    grade: null,
    roles: [],
    azureId: '',
    hireDate: '',
    resignDate: null,
    resignReason: '',
    resignReasonDetails: '',
    contractType: '',
    // ADR-136: contract number captured at hire only. Stamped onto the new
    // employment episode as ADR-049 sourceReference { source: 'radar', contractId }.
    // Never sent on update/rehire — the episode owns it after creation.
    contractNumber: '',
    // ADR-136 phase 4: optional fixed-term capture on hire/rehire — end date + the
    // A2 auto-populated, editable comment. Never sent on an ordinary update:
    // changing an existing contract's end is the phase-5 verb, not an edit.
    contractEndDate: null,
    contractEndComment: '',
    engagementModel: '',
    contractCompany: '',
    workingLocation: '',
    cluster: '',
    projects: [],
    employeeLeafSegments: [],
    directManager: null,
    budgetDirector: null,
    myExec: null, // ADR-127
  });

  const [roles, setRoles] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  // ADR-149 §5.2 — the person verb being performed from the header, if any.
  const [personAction, setPersonAction] = useState(null);

  // Read ?tab=... from the URL on mount (deep-linking from the Bench Report and similar).
  // The `readonly` query parameter is no longer consumed (ADR-009 replaced the blanket
  // read-only mode with per-activity authorization driven by the API response).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    if (requestedTab && Object.values(TABS).includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
    // Run once per location change — that lets back/forward navigation update state too.
  }, [location.search]);

  // Check if user can view instant feedback (has manage permission or is viewing their own profile)
  const canViewInstantFeedback = () => {
    
    // Create a feedback object with the correct ID (the ID of the employee being viewed)
    const feedbackObject = { 
      employeeId: id, 
      __type: SUBJECTS.INSTANT_FEEDBACK 
    };
    
    // Check if user has manage permission for InstantFeedback for this specific employee
    return ability.can(ACTIONS.MANAGE, feedbackObject);
    
  };

  const canViewAssignments = () => {

    const assignmentObject = {
      clusterId: employeeData?.cluster?.id,
      __type: SUBJECTS.EMPLOYEE_ASSIGNMENT
    };

    return ability.can(ACTIONS.READ, assignmentObject);
  };

  const canViewEmploymentEpisodes = () => {
    const episodeObject = {
      employeeId: id,
      __type: SUBJECTS.EMPLOYMENT_EPISODE
    };

    return ability.can(ACTIONS.READ, episodeObject);
  };

  const canViewPlacementActivity = () => {
    return ability.can(ACTIONS.READ, SUBJECTS.PLACEMENT_ACTIVITY);
  };

  // ADR-017: resignation fields reveal/lock on the lifecycle fact, not the
  // display status — outside-window pending resignations behave identically
  const hasPendingResignation = () => Boolean(formData.hasPendingResignation);

  // Fetch employee data
  // ADR-113 addendum — `mode` is a dependency because /employees/:id/edit and
  // /employees/:id/edit/:mode are SIBLING ROUTES rendering this same
  // component: React preserves the instance across the transition (no
  // remount). Entering or leaving replace/rehire must therefore refetch, so
  // formData is re-shaped for the mode (rehire blanks hireDate/resign fields;
  // ordinary edit restores them) and the replace guards
  // (pendingRenewalCutover, originalContractStartDate) are fresh.
  useEffect(() => {
    fetchMetadata();
    if (id) {
      fetchEmployee();
    }
  }, [id, mode, requestedReplaceEpisodeId]);

  // ADR-113 addendum — same no-remount reason: the Contracts tab is the
  // replace flow's launcher, so on entry the preserved instance may still sit
  // on CONTRACTS while the tab bar hides — force the successor form. Leaving
  // the flow is handled by the ?tab effect above (the return navigation
  // carries ?tab=CONTRACTS).
  useEffect(() => {
    if (isRehireMode) {
      setActiveTab(TABS.EMPLOYMENT_INFORMATION);
    }
  }, [isRehireMode]);

  // Handle position selection and grade requirements
  const handlePositionChange = (positionId, positionData) => {
    setFormData(prev => ({
      ...prev,
      position: positionId
    }));
    
    // If switching to a position that doesn't require grade, clear the grade
    if (positionData && !positionData.requiresGrade && formData.grade) {
      setFormData(prev => ({
        ...prev,
        grade: null
      }));
    }
  };

  // Handle when the selected position object changes (for grade visibility)
  const handleSelectedPositionChange = (positionData) => {
    setSelectedPosition(positionData);
  };



  const fetchMetadata = async () => {
    try {
      const promises = [
        organizationApi.getAllRoles(),
        employeesApi.getAllEmployees(),
      ];

      // Only fetch projects if in edit mode
      if (id) {
        promises.push(projectsApi.getAllProjects());
      }

      const [
        rolesData,
        employeesData,
        ...rest
      ] = await Promise.all(promises);
      
      setRoles(rolesData.filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL));
      setEmployees(employeesData);
      
      // Set projects only if we're in edit mode
      if (id && rest.length > 0) {
        setProjects(rest[0]);
      }
    } catch (err) {
      showAlert('Failed to load form data', 'error', err);
    }
  };

  const fetchEmployee = async () => {
    try {
      setIsLoading(true);
      const employee = await employeesApi.getEmployeeById(id);

      // Replace mode — resolve the contract being replaced before anything is
      // shown. A named contract that is not an open contract of this person is
      // refused: falling back to the primary would replace the wrong contract.
      let replaceFacts = null;
      if (isReplaceMode) {
        const { contract: replaceTarget, notFound } = resolveReplaceTarget(employee, requestedReplaceEpisodeId);
        if (notFound) {
          showAlert('The contract to replace is not an open contract of this person — start again from the Contracts tab', 'error');
          navigate(`/employees/${id}/edit?tab=CONTRACTS`, { replace: true });
          return;
        }
        replaceFacts = replaceTargetFacts(employee, replaceTarget);
        setReplaceTargetEpisodeId(replaceFacts.episodeId);
        setReplaceBounds(replaceTargetBounds(employee, replaceTarget));
        setReplaceTargetLabel(replaceTarget
          ? contractIdentityLabel(null, {
            startDate: replaceTarget.startDate || null,
            payload: { sourceReference: replaceTarget.contractNumber ? { contractId: replaceTarget.contractNumber } : {} },
            contractType: replaceTarget.contractType || null,
            contractCompany: replaceTarget.contractCompany || null,
          })
          : '');
      } else {
        setReplaceTargetEpisodeId(null);
        setReplaceTargetLabel('');
        setReplaceBounds({ isSecondary: false, ownEndYmd: null, primaryEndYmd: null });
      }

      setEmployeeData(employee);

      // Check if resignReason is valid, otherwise move it to resignReasonDetails
      const validResignReasons = Object.values(RESIGN_REASONS);
      let resignReason = employee.resignReason || '';
      let resignReasonDetails = employee.resignReasonDetails || '';

      if (!isRehireMode && resignReason && !validResignReasons.includes(resignReason)) {
        resignReasonDetails = resignReason;
        resignReason = RESIGN_REASONS.OTHER;
      }

      setFormData({
        id: employee.id,
        fullName: employee.fullName,
        email: employee.email,
        position: employee.position?.id || '',
        grade: employee.grade?.id || null,
        roles: employee.roles?.map(role => role.id) || [],
        azureId: employee.azureId || '',
        organizationalStatus: employee.organizationalStatus,
        // ADR-017: lifecycle fact for action gates — true for any pending
        // resignation, inside or outside the display window
        // In replace mode, the REPLACED contract's own pending end (it is
        // what withdrawScheduledEnd withdraws), not the person-level flag.
        hasPendingResignation: replaceFacts
          ? replaceFacts.hasPendingResignation
          : Boolean(employee.hasPendingResignation),
        // ADR-136: always present so the Contract Number input stays controlled.
        // A rehire starts a NEW contract, so it never inherits the previous
        // episode's number — blank in both modes.
        contractNumber: '',
        // ADR-136 phase 4: same reasoning — the fixed-term fields describe the NEW
        // contract being started, never an existing one, so they load blank.
        contractEndDate: null,
        contractEndComment: '',
        // Reset these fields in rehire mode
        hireDate: isRehireMode ? '' : (employee.hireDate || ''),
        resignDate: isRehireMode ? null : (employee.resignDate || null),
        resignReason: isRehireMode ? '' : resignReason,
        resignReasonDetails: isRehireMode ? '' : resignReasonDetails,
        // End rehire mode
        // Replace pre-fills the successor with the REPLACED contract's terms
        // (easier to adjust than to re-enter); elsewhere, the person view.
        contractType: replaceFacts ? replaceFacts.contractTypeId : (employee.contractType?.id || ''),
        engagementModel: replaceFacts ? replaceFacts.engagementModelId : (employee.engagementModel?.id || ''),
        contractCompany: replaceFacts ? replaceFacts.contractCompanyId : (employee.contractCompany?.id || ''),
        workingLocation: employee.workingLocation?.id || '',
        cluster: employee.cluster?.id || '',
        projects: employee.projects || [],
        timeInOrganization: employee.timeInOrganization || null,
        employeeLeafSegments: employee.employeeLeafSegments || [],
        directManager: employee.directManager || null,
        budgetDirector: employee.budgetDirector || null,
        myExec: employee.myExec || null, // ADR-127
      });

      // ADR-138 — remembered separately from formData, which rehire mode blanks.
      setOriginalHireDate(employee.hireDate || null);
      setOriginalResignDate(employee.resignDate || null);
      // ADR-136 phase 5 — the running contract's start (episode HIRE_FINALIZED),
      // floor for the replace cutover.
      setOriginalContractStartDate(replaceFacts
        ? replaceFacts.startDate
        : (employee.contractStartDate || employee.hireDate || null));
      // ADR-136 phase 6 (§15 slice 3) — episode ownership + any scheduled renewal
      setCurrentEpisodeSource(replaceFacts ? replaceFacts.source : (employee.currentEpisodeSource || null));
      setPendingRenewalCutover(replaceFacts ? replaceFacts.pendingRenewalOn : (employee.pendingRenewal?.effectiveOn || null));

      // Set the selected position data for grade requirement checking
      if (employee.position) {
        setSelectedPosition(employee.position);
      }

      // ADR-109 §1 — auto route: resolve Edit/View with the Individuals-list rule and
      // replace the entry, keeping ?tab= and the caller's return state (I4). The form
      // stays mounted (same id), so the data loaded above is reused — no second fetch.
      if (isAutoMode) {
        const mode = resolveIndividualOpenMode({ employee, canManageEmployee });
        const { path, options } = buildResolvedIndividualNavigation({
          employeeId: id,
          mode,
          search: location.search,
          state: location.state,
        });
        navigate(path, options);
      }

    } catch (err) {
      showAlert('Failed to load employee data', 'error', err);
      navigate('/employees');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      const requiredFields = [
        'fullName', 'email', 'contractType', 'contractCompany', 
        'workingLocation', 'cluster', 'position', 'hireDate'
      ];

      // Add grade to required fields only if position requires it
      if (selectedPosition?.requiresGrade) {
        requiredFields.push('grade');
      }
      
      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        showAlert(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        return;
      }

      // ADR-136 phase 4: fixed-term validation — end strictly after start, comment
      // present. Only relevant when an end date was set; open-ended hires skip this.
      if (!id && formData.contractEndDate) {
        if (!dayjs.utc(formData.contractEndDate).isAfter(dayjs.utc(formData.hireDate), 'day')) {
          showAlert('Contract end date must be after the hire date', 'error');
          return;
        }
        if (!String(formData.contractEndComment || '').trim()) {
          showAlert('Please provide the fixed-term comment', 'error');
          return;
        }
      }

      // Format dates and exclude computed/read-only fields
      const {
        employeeLeafSegments, directManager, budgetDirector,
        myExec: _myExec, // ADR-127: myExec is read-only
        contractNumber, contractEndDate, contractEndComment,
        ...dataToSubmit
      } = formData;
      const formattedData = {
        ...dataToSubmit,
        hireDate: formData.hireDate ? dayjs.utc(formData.hireDate).format('YYYY-MM-DD') : null,
        resignDate: formData.resignDate ? dayjs.utc(formData.resignDate).format('YYYY-MM-DD') : null
      };

      // Remove null/undefined values to avoid validation errors with optional fields
      Object.keys(formattedData).forEach(key => {
        if (formattedData[key] === null || formattedData[key] === undefined) {
          delete formattedData[key];
        }
      });

      if (id) {
        // ADR-142 §5.4 — the employee row no longer accepts contract terms;
        // they live on the episode and change by amendment. Stripped here so
        // the payload says what it means (the API ignores them regardless).
        delete formattedData.contractType;
        delete formattedData.contractCompany;
        delete formattedData.engagementModel;
        await employeesApi.updateEmployee(id, formattedData);
        showAlert('Individual updated successfully', 'success');
        // Update the state in the store
        if (onEmployeeUpdate) {
          onEmployeeUpdate();
        }
      } else {
        // ADR-136: the contract number only rides the hire batch, and only when
        // filled in — omitted, the emitted events are byte-for-byte today's.
        // ADR-136 phase 4: same rule for the fixed-term fields — present, they
        // extend the batch with the production resign legs; absent, nothing changes.
        await hire({
          ...formattedData,
          ...(contractNumber && contractNumber.trim() ? { contractNumber: contractNumber.trim() } : {}),
          ...(contractEndDate ? {
            contractEndDate: dayjs.utc(contractEndDate).format('YYYY-MM-DD'),
            contractEndReason: String(contractEndComment).trim()
          } : {}),
        });
        showAlert('Individual created successfully', 'success');
      }
      // ADR-109 I3 — opened from a report: saving returns there (with its state), like Back.
      if (id && location.state?.returnTo) {
        navigate(location.state.returnTo, { state: location.state });
      } else {
        navigate('/employees');
      }
    } catch (err) {
      // ADR-171 C4 (ADR-144 §17) — an existing person is a routing decision,
      // not a dead end: open their contracts, where Add Contract lives.
      const existingEmployeeId = err?.data?.reasonCode === 'ALREADY_EMPLOYEE'
        ? err.data.refusal?.existingEmployeeId
        : null;
      if (!id && existingEmployeeId) {
        showAlert('This individual is already in radar — showing their contracts. Use Add Contract to give them another one.', 'info');
        navigate(`/employees/${existingEmployeeId}/edit?tab=CONTRACTS`);
        return;
      }
      showAlert(err.message || 'Failed to save individual', 'error', err);
    }
  };

  const handleRehire = async () => {
    try {
      const requiredFields = [
        'fullName', 'email', 'contractType', 'contractCompany',
        'workingLocation', 'cluster', 'position', 'hireDate'
      ];

      // Add grade to required fields only if position requires it
      if (selectedPosition?.requiresGrade) {
        requiredFields.push('grade');
      }

      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        showAlert(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        return;
      }

      // ADR-136 phase 4: fixed-term validation, symmetric with hire — against the
      // CONTRACT START date (formData.hireDate in rehire mode).
      if (formData.contractEndDate) {
        if (!dayjs.utc(formData.contractEndDate).isAfter(dayjs.utc(formData.hireDate), 'day')) {
          showAlert('Contract end date must be after the contract start date', 'error');
          return;
        }
        if (!String(formData.contractEndComment || '').trim()) {
          showAlert('Please provide the fixed-term comment', 'error');
          return;
        }
      }

      // Format dates and exclude computed/read-only fields
      // ADR-136: contractNumber is pulled out of the generic employee payload and
      // re-attached to the rehire call below, where it rides the REHIRE event.
      const {
        employeeLeafSegments, directManager, budgetDirector,
        myExec: _myExec, // ADR-127: myExec is read-only
        contractNumber, contractEndDate, contractEndComment,
        ...dataToSubmit
      } = formData;
      const formattedData = {
        ...dataToSubmit,
        hireDate: formData.hireDate ? dayjs.utc(formData.hireDate).format('YYYY-MM-DD') : null,
      };

      // Remove null/undefined values to avoid validation errors with optional fields
      Object.keys(formattedData).forEach(key => {
        if (formattedData[key] === null || formattedData[key] === undefined) {
          delete formattedData[key];
        }
      });

      // ADR-139 — the strip above removes nulls, but for GRADE a null is meaningful:
      // it says the new contract's position has no grade concept. Re-attached
      // explicitly so the REHIRE handler clears the singleton instead of falling back
      // to the outgoing contract's grade and opening a leg under a position that has
      // none (employee.service.js rehire branch). A graded position sends the id as
      // before, so nothing changes on the ordinary path.
      formattedData.grade = (selectedPosition?.requiresGrade && formData.grade)
        ? formData.grade
        : null;

      // ADR-138: the hire date written onto the EMPLOYEE RECORD travels separately
      // from `hireDate` above, which is this rehire's contract start date. "Keep
      // current" sends nothing, so that path emits the pre-ADR-138 batch unchanged.
      const employeeHireDate =
        hireDateSource.kind === HIRE_DATE_SOURCE_KIND.KEEP ? null : resolvedHireDateSourceDate;

      await rehireEmployee({
        employeeId: id,
        ...formattedData,
        // ADR-136: rides the REHIRE event so it lands on the episode that event
        // creates; omitted entirely when blank.
        ...(contractNumber && contractNumber.trim() ? { contractNumber: contractNumber.trim() } : {}),
        ...(employeeHireDate ? { employeeHireDate } : {}),
        // ADR-136 phase 4: fixed-term rehire — the new contract's scheduled end.
        ...(contractEndDate ? {
          contractEndDate: dayjs.utc(contractEndDate).format('YYYY-MM-DD'),
          contractEndReason: String(contractEndComment).trim()
        } : {}),
      });

      showAlert('Individual rehired successfully', 'success');
      // Navigate back with state to trigger a reload
      navigate('/employees', {
        state: {
          forceRefresh: true,
          fromRehire: true
        }
      });
    } catch (err) {
      showAlert(err.message || 'Failed to rehire individual', 'error', err);
    }
  };

  // ADR-136 phase 5 — Replace Contract submit: the full §5a batch via
  // replaceContract(). The cancel is enacted AT the cutover (today/past only)
  // and the successor lands in the same transaction — no partial states, and
  // no backend or radar-ingestion changes anywhere (§2a.7): every event and
  // processOption in the batch is one the syncs already use.
  //
  // ADR-136 phase 6 (§15 slice 3): a FUTURE cutover no longer rejects — it
  // schedules. Same form, same fields, but the submit emits ONE
  // RENEWAL_SCHEDULED event via scheduleReplacement(); nothing changes until
  // the sweep enacts it on the cutover date, and it stays cancellable until
  // then (Cancel Scheduled Renewal on the card).
  const handleReplace = async () => {
    try {
      const requiredFields = [
        'fullName', 'email', 'contractType', 'contractCompany',
        'workingLocation', 'cluster', 'position', 'hireDate'
      ];
      if (selectedPosition?.requiresGrade) {
        requiredFields.push('grade');
      }
      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        showAlert(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        return;
      }

      if (!replaceCutoverDate) {
        showAlert('Please set the cutover date', 'error');
        return;
      }
      // ADR-179 D2 — the cutover is the old contract's LAST DAY: a cutover of
      // today or later is scheduled (the sweep switches the morning after);
      // only a past (backdated) cutover replaces immediately.
      const isFutureCutover = replaceCutoverDate >= dayjs().format('YYYY-MM-DD');
      if (isFutureCutover && pendingRenewalCutover) {
        showAlert('A replacement is already scheduled for this contract — cancel it first (Cancel Scheduled Renewal on the card)', 'error');
        return;
      }
      if (originalContractStartDate
          && dayjs.utc(replaceCutoverDate).isBefore(dayjs.utc(originalContractStartDate), 'day')) {
        showAlert('The cutover date cannot be before the current contract start', 'error');
        return;
      }
      if (!replaceReason.trim()) {
        showAlert('Please provide the replacement reason', 'error');
        return;
      }
      if (!formData.hireDate || formData.hireDate <= replaceCutoverDate) {
        showAlert('The new contract must start after the cutover date', 'error');
        return;
      }
      // ADR-188 D4 — a secondary's successor stays within the primary
      const successorProblem = replaceSuccessorProblem(replaceBounds, {
        startYmd: formData.hireDate || null,
        endYmd: formData.contractEndDate || null
      });
      if (successorProblem) {
        showAlert(successorProblem, 'error');
        return;
      }
      if (formData.contractEndDate) {
        if (!dayjs.utc(formData.contractEndDate).isAfter(dayjs.utc(formData.hireDate), 'day')) {
          showAlert('Contract end date must be after the contract start date', 'error');
          return;
        }
        if (!String(formData.contractEndComment || '').trim()) {
          showAlert('Please provide the fixed-term comment', 'error');
          return;
        }
      }

      // ADR-138 — same source semantics as rehire: "keep current" sends nothing.
      const employeeHireDate =
        hireDateSource.kind === HIRE_DATE_SOURCE_KIND.KEEP ? null : resolvedHireDateSourceDate;

      // Identical payload either way — the stored intent must be exactly what
      // the immediate verb would have sent, so enactment cannot drift.
      // ADR-155 §6 — the OUTGOING contract this replacement ends: the one
      // resolved on load (?contract= / launcher state, else the primary) — the
      // same contract every guard on this form was checked against.
      const outgoingEpisodeId = replaceTargetEpisodeId;

      const replacementPayload = {
        employeeId: id,
        ...(outgoingEpisodeId ? { employmentEpisodeId: outgoingEpisodeId } : {}),
        cutoverDate: replaceCutoverDate,
        reason: replaceReason.trim(),
        keepProjects,
        // Withdraw the running contract's scheduled end first, when one exists.
        // (For a scheduled replacement this is re-decided from fire-time state
        // by the sweep — the flag only records the intent as entered.)
        withdrawScheduledEnd: Boolean(formData.hasPendingResignation),
        position: formData.position,
        // ADR-139 — an explicit null (not an omitted key) when the successor's position
        // requires no grade: absent means "unchanged" to the REHIRE handler, which would
        // carry the outgoing contract's grade onto the new one. Same rule on the
        // scheduled path — the stored intent must say it too, since the phase-6 sweep
        // replays this payload verbatim.
        grade: (selectedPosition?.requiresGrade && formData.grade) ? formData.grade : null,
        contractType: formData.contractType,
        contractCompany: formData.contractCompany,
        // ADR-179 §8 — the successor's engagement model as shown on the form
        // (pre-filled from the replaced contract, ADR-177 D3); omitted = inherit.
        ...(formData.engagementModel ? { engagementModel: formData.engagementModel } : {}),
        workingLocation: formData.workingLocation,
        cluster: formData.cluster,
        hireDate: dayjs.utc(formData.hireDate).format('YYYY-MM-DD'),
        ...(formData.contractNumber && formData.contractNumber.trim()
          ? { contractNumber: formData.contractNumber.trim() } : {}),
        ...(employeeHireDate ? { employeeHireDate } : {}),
        ...(formData.contractEndDate ? {
          contractEndDate: dayjs.utc(formData.contractEndDate).format('YYYY-MM-DD'),
          contractEndReason: String(formData.contractEndComment).trim()
        } : {}),
      };

      if (isFutureCutover) {
        await scheduleReplacement(replacementPayload);
        // ADR-182 D3 (S7) — both dates stated
        showAlert(`Contract replacement scheduled: the current contract's last day is ${eventDayFmt(replaceCutoverDate)}, the switch runs on the morning of ${eventDayFmt(renewalSwitchDay(replaceCutoverDate))} — cancellable until then`, 'success');
      } else {
        await replaceContract(replacementPayload);
        showAlert('Contract replaced successfully', 'success');
      }

      // ADR-113 addendum — completion returns WHERE THE FLOW STARTED. The
      // Contracts tab launches Replace with returnTo pointing back at itself,
      // so the operator lands on the contract card(s) showing the result
      // (the tab remounts and fetches fresh — /edit and /edit/:mode are
      // sibling routes). The person's repository card is refreshed through
      // the store (the same mechanism ordinary Edit uses), replacing the
      // list-wide forceRefresh for this path. A card-launched replace keeps
      // the original list navigation.
      const returnTo = location.state?.returnTo;
      if (returnTo) {
        if (onEmployeeUpdate) onEmployeeUpdate();
        const { returnTo: _consumedTo, returnLabel: _consumedLabel, replaceEpisodeId: _consumedEpisode, ...restState } = location.state || {};
        navigate(returnTo, { state: restState });
      } else {
        navigate('/employees', {
          state: {
            forceRefresh: true,
            fromRehire: true
          }
        });
      }
    } catch (err) {
      showAlert(err.message || 'Failed to replace the contract', 'error', err);
    }
  };

  // ADR-138 — the contract start date being entered for this rehire, and the date the
  // current hire-date-source choice actually resolves to. Derived rather than stored:
  // the Contract Start Date field stays editable after the picker is closed, so a
  // "use this rehire's start date" choice must follow it rather than freeze at
  // whatever it was when the operator confirmed.
  const rehireContractStartDate = formData.hireDate
    ? dayjs.utc(formData.hireDate).format('YYYY-MM-DD')
    : null;
  const resolvedHireDateSourceDate =
    hireDateSource.kind === HIRE_DATE_SOURCE_KIND.NEW
      ? rehireContractStartDate
      : hireDateSource.kind === HIRE_DATE_SOURCE_KIND.KEEP
        ? toEpisodeDayKey(originalHireDate)
        : hireDateSource.date;

  // ADR-136 phase 6 (§15 slice 3) — a scheduled cutover flips the whole replace
  // surface into SCHEDULING mode: the alert, the button, and the submit path.
  // ADR-179 D2 — today counts as scheduled: the old contract is in force today.
  const isScheduledReplacement = Boolean(
    isReplaceMode && replaceCutoverDate && replaceCutoverDate >= dayjs().format('YYYY-MM-DD')
  );
  // §15 decision 4 — warn (never block) when the running contract belongs to an
  // ingestion source: at enactment the sync planner defers and reconciles, same
  // as a manual rehire today, but the operator should know the sync owns it.
  const isIngestionOwnedContract = !isRadarSourced({ source: currentEpisodeSource });

  // ADR-138 — pre-select the hire date source from the gap discriminator, and keep it
  // in step while the contract start date is still being typed. A REAL break (more
  // than the one-day boundary between a leg ending and the next starting) means
  // employment restarts, so the rehire's own start date is the answer that makes the
  // ADR-017 status reading correct. A contiguous renewal means the person never left,
  // so keeping the current hire date is the answer that does not reset their tenure.
  // Only a default: once the operator opens the picker and chooses, this stops firing.
  useEffect(() => {
    if (!isRehireMode || hireDateSourceTouched) return;

    const contractStart = toEpisodeDayKey(formData.hireDate);
    if (!contractStart) return;

    const currentHire = toEpisodeDayKey(originalHireDate);
    // ADR-136 phase 5: in replace mode the previous contract ends at the CUTOVER,
    // not at the stored resignDate — the gap discriminator compares against it.
    const previousEnd = toEpisodeDayKey(isReplaceMode ? replaceCutoverDate : originalResignDate);
    const gapped =
      !currentHire ||
      !previousEnd ||
      dayjs.utc(contractStart).diff(dayjs.utc(previousEnd), 'day') > 1;

    setHireDateSource(
      gapped
        ? { kind: HIRE_DATE_SOURCE_KIND.NEW, episodeId: null, date: contractStart }
        : { kind: HIRE_DATE_SOURCE_KIND.KEEP, episodeId: null, date: currentHire }
    );
  }, [isRehireMode, isReplaceMode, replaceCutoverDate, hireDateSourceTouched, formData.hireDate, originalHireDate, originalResignDate]);

  // ADR-136 phase 5 — replace-mode defaults derived from the cutover: the
  // successor start defaults to cutover + 1 (the ADR-090 no-gap-day convention)
  // until the operator edits the start date, and keep-projects defaults per the
  // gap discriminator (§2a settlement #7: contiguous → keep, gapped → close)
  // until the operator touches the checkbox.
  useEffect(() => {
    if (!isReplaceMode || replaceStartTouched || !replaceCutoverDate) return;
    const defaultStart = dayjs.utc(replaceCutoverDate).add(1, 'day').format('YYYY-MM-DD');
    // formData.hireDate is a dependency on purpose: the employee load blanks it
    // (rehire-mode initialization) AFTER this effect first fires, so the default
    // must re-apply whenever the field is empty and the operator hasn't touched it.
    setFormData(prev => (prev.hireDate === defaultStart ? prev : { ...prev, hireDate: defaultStart }));
  }, [isReplaceMode, replaceStartTouched, replaceCutoverDate, formData.hireDate]);

  useEffect(() => {
    if (!isReplaceMode || keepProjectsTouched || !replaceCutoverDate || !formData.hireDate) return;
    const contiguous = dayjs.utc(formData.hireDate).diff(dayjs.utc(replaceCutoverDate), 'day') <= 1;
    setKeepProjects(contiguous);
  }, [isReplaceMode, keepProjectsTouched, replaceCutoverDate, formData.hireDate]);

  // ADR-136 phase 4 (A2) — the fixed-term comment is generated at the entry
  // surface. ADR-173 D2 moved the generator + touched-pattern into the shared
  // FixedTermFields; the form only holds the values.
  const handleContractEndDateChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, contractEndDate: value }));
  }, []);
  const handleContractEndCommentChange = useCallback((value) => {
    setFormData(prev => (prev.contractEndComment === value ? prev : { ...prev, contractEndComment: value }));
  }, []);

  // ADR-138 — one picker, two outcomes. On rehire the choice is held in form state and
  // travels with the REHIRE event on submit (there is no episode to point at yet). On
  // the Employment Information tab the episode already exists, so it is applied
  // immediately through the narrow endpoint.
  const handleHireDateSourceConfirm = async (selection) => {
    if (isRehireMode) {
      setHireDateSource(selection);
      setHireDateSourceTouched(true);
      setEpisodePickerOpen(false);
      return;
    }

    if (!selection.episodeId) return;

    try {
      setIsApplyingHireDate(true);
      await employeesApi.setHireDateFromEpisode(id, selection.episodeId);
      setEpisodePickerOpen(false);
      showAlert('Hire date updated from the selected employment episode', 'success');
      // Re-fetch rather than patch local state: the hire date drives derived values
      // (timeInOrganization, organizationalStatus) that the update response does not
      // carry, and a stale tenure line next to a freshly changed date is exactly the
      // kind of quiet wrongness this ADR is about.
      await fetchEmployee();
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (err) {
      showAlert(err.message || 'Failed to update the hire date', 'error', err);
    } finally {
      setIsApplyingHireDate(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Use effect for the debounced search
  useEffect(() => {
    if (debouncedSearchQuery) {
      handleAzureSearch(debouncedSearchQuery);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery]);

  const handleAzureSearch = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const searchResponse = await employeesApi.searchAzureUsers(query);
      setSearchResults(searchResponse);

    } catch (error) {
      showAlert('Failed to search Azure AD users: ' + error.message, 'error', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      azureId: user.id,
      fullName: user.displayName,
      email: user.email
    }));
    setSearchModalOpen(false);
    setSearchQuery('');
  };

  const renderEmploymentInformation = () => (
    <FormContainer>
      <FormSection>
        <SectionTitle>Basic Information</SectionTitle>
        <Box sx={{ 
          display: 'flex',
          gap: 4,
          alignItems: 'flex-start'
        }}>
          <Box sx={{ pt: 1 }}>
            <EmployeeAvatar 
              employee={{ 
                fullName: formData.fullName,
                azureId: formData.azureId
              }}
              size={160}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormRow>
              <TextField
                fullWidth
                autoComplete="off"
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                required
                disabled={isViewMode}
              />
              <TextField
                fullWidth
                autoComplete="off"
                label="Email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                disabled={isViewMode}
              />
            </FormRow>
            <FormRow>
              <TextField
                fullWidth
                autoComplete="off"
                label="Azure ID"
                value={formData.azureId}
                onClick={() => !isViewMode && setSearchModalOpen(true)}
                slotProps={{
                  input: {
                    readOnly: true,
                  }
                }}
                slots={{
                  endAdornment: () => !isViewMode && (
                    <InputAdornment position="end">
                      <FaSearch style={{ color: '#64748B', cursor: 'pointer' }} />
                    </InputAdornment>
                  ),
                }}
                disabled={isViewMode}
              />
              {id && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center', 
                  gap: 1,
                  width: '100%'
                }}>
                  <Chip
                    label={getStatusText(formData.organizationalStatus)}
                    sx={{
                      fontSize: '16px',
                      backgroundColor: getStatusColor(formData.organizationalStatus),
                      color: getStatusTextColor(formData.organizationalStatus),
                      fontWeight: 500
                    }}
                  />
                </Box>
              )}
            </FormRow>
            {id && formData.timeInOrganization && (
              <Box sx={{ 
                color: '#64748B',
                fontSize: '14px',
                fontStyle: 'italic',
                mt: 1
              }}>
                {formatTimeDuration(formData.timeInOrganization, 'in the Organization')}
              </Box>
            )}
          </Box>
        </Box>
      </FormSection>

      {/* ADR-136 phase 5 — Replace Contract (§5a supersession): the cutover leg.
          The successor's attributes are the ordinary rehire-style fields below.
          Phase 6 (§15 slice 3): a FUTURE cutover switches this section into
          scheduling mode instead of rejecting. */}
      {isReplaceMode && !isViewMode && (
        <FormSection>
          <SectionTitle>Contract Replacement</SectionTitle>
          {/* ADR-188 D5 — the person details below are not offered: replacing
              a secondary contract leaves the person as they are */}
          {isSecondaryReplace && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Replacing a secondary contract changes only that contract — person details
              (hire date, position, grade, region, working location) stay as they are.
              {replaceBounds.primaryEndYmd && (
                <> The primary contract ends on <strong>{eventDayFmt(replaceBounds.primaryEndYmd)}</strong>:
                {' '}the new contract must end on or before it.</>
              )}
            </Alert>
          )}
          {isScheduledReplacement ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              This <strong>schedules the replacement</strong>: the current contract stays in force
              through <strong>{eventDayFmt(replaceCutoverDate)}</strong> (its last day);
              the switch runs automatically on the morning
              of <strong>{eventDayFmt(renewalSwitchDay(replaceCutoverDate))}</strong>.
              {formData.hireDate ? <> The new contract below starts on <strong>{eventDayFmt(formData.hireDate)}</strong>.</> : null}
              {' '}Until then it appears on the card as a scheduled renewal and can be removed
              with <em>Cancel Scheduled Renewal</em>. Only a cutover in the past replaces
              immediately.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This <strong>ends the current contract at the cutover date and starts the new one
              below</strong> — one transaction.
              {formData.hasPendingResignation
                ? ' The current contract’s scheduled end is withdrawn first, then the'
                : ' The'}
              {' '}old episode is closed at the cutover with a &quot;replaced&quot; note.
              The cutover is in the past, so this applies <strong>now</strong>. A cutover of
              today or later schedules the replacement instead.
            </Alert>
          )}
          {pendingRenewalCutover && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A replacement is <strong>already scheduled
              for {dayjs.utc(pendingRenewalCutover).format('DD/MM/YYYY')}</strong> — cancel it
              (Cancel Scheduled Renewal on the card) before scheduling another one.
            </Alert>
          )}
          {isIngestionOwnedContract && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The current contract is <strong>maintained by an ingestion
              source ({currentEpisodeSource})</strong>. You can still replace or schedule here,
              but that source stays authoritative for its own records — at enactment the sync
              defers and reconciles on its next run, exactly as for a manual rehire.
            </Alert>
          )}
          <FormRow>
            <DatePicker
              label="Cutover Date (current contract ends)"
              value={replaceCutoverDate ? dayjs.utc(replaceCutoverDate) : null}
              onChange={(newValue) => {
                if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                  setReplaceCutoverDate(newValue.format('YYYY-MM-DD'));
                } else if (newValue === null) {
                  setReplaceCutoverDate(null);
                }
              }}
              minDate={originalContractStartDate ? dayjs.utc(originalContractStartDate) : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !replaceCutoverDate,
                  helperText: !replaceCutoverDate
                    ? 'Cutover date is required'
                    : (isScheduledReplacement
                      ? `Last day of the current contract — the replacement is SCHEDULED and switches on the morning of ${eventDayFmt(renewalSwitchDay(replaceCutoverDate))}`
                      : 'Past date — the current contract is closed with this end date now'),
                  inputProps: { placeholder: 'DD/MM/YYYY' }
                }
              }}
            />
            <TextField
              fullWidth
              label="Replacement Reason"
              value={replaceReason}
              onChange={(e) => setReplaceReason(e.target.value)}
              required
              error={!replaceReason.trim()}
              helperText={!replaceReason.trim()
                ? 'Reason is required'
                : 'Recorded on the old contract’s closing note'}
              placeholder="Why the contract is being replaced..."
            />
          </FormRow>
          {/* ADR-188 D4 (c) — a gapped replacement is allowed; say it is one */}
          {replaceGapWarning(replaceBounds, replaceCutoverDate) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {replaceGapWarning(replaceBounds, replaceCutoverDate)}.
            </Alert>
          )}
          {!isSecondaryReplace && (
          <>
          <FormControlLabel
            control={(
              <Checkbox
                checked={keepProjects}
                onChange={(e) => {
                  setKeepProjectsTouched(true);
                  setKeepProjects(e.target.checked);
                }}
              />
            )}
            label={keepProjects
              ? 'Keep project allocations across the replacement'
              : 'Keep project allocations across the replacement (currently OFF — they will be closed at the cutover date)'}
          />
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
            Default follows the gap: a contiguous replacement (new contract starts the day
            after the cutover) keeps allocations; a gapped one closes them at the cutover.
          </Typography>
          </>
          )}
        </FormSection>
      )}

      <FormSection>
        <SectionTitle>Employment Details</SectionTitle>
        <FormRow>
          <DatePicker
            label={isRehireMode ? 'Contract Start Date' : 'Hire Date'}
            value={formData.hireDate ? dayjs.utc(formData.hireDate) : null}
            onChange={(newValue) => {
              // ADR-136 phase 5: once the operator edits the successor start,
              // the cutover-derived default stops firing.
              if (isReplaceMode) setReplaceStartTouched(true);
              // Only update if we have a valid date
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                handleInputChange('hireDate')({
                  target: { value: newValue.format('YYYY-MM-DD') }
                });
              } else if (newValue === null) {
                handleInputChange('hireDate')({
                  target: { value: null }
                });
              }
            }}
            disabled={isViewMode || (!!id && !isRehireMode && !canEditHireDate)}
            // ADR-136 phase 5: in replace mode the successor cannot start on or
            // before the cutover — the picker enforces what the wrapper's schema
            // refines (hireDate > cutoverDate).
            minDate={isReplaceMode && replaceCutoverDate
              ? dayjs.utc(replaceCutoverDate).add(1, 'day')
              : undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: Boolean(isReplaceMode && formData.hireDate && replaceCutoverDate
                  && formData.hireDate <= replaceCutoverDate),
                helperText: isReplaceMode
                  ? (formData.hireDate && replaceCutoverDate && formData.hireDate <= replaceCutoverDate
                    ? 'The new contract must start after the cutover date'
                    : 'Defaults to the day after the cutover (contiguous replacement)')
                  : undefined,
                inputProps: {
                  placeholder: 'DD/MM/YYYY'
                }
              }
            }}
          />
          {/* ADR-142 §5.4 — contract terms are create/rehire-only, exactly as
              contractNumber/contractEndDate already are: changing them on a
              live contract is an amendment (ADR-143), and a data error is
              corrected on the Employment Episodes tab (ADR-019). On plain
              edit/view the selectors are not rendered at all — the read-only
              contract strip below and the Contracts tab carry the terms. */}
          {showTermSelectors && (
            <>
              {/* ADR-173 D1 — the shared contract-term fields (one configuration
                  with Add Contract and Amend Contract). */}
              <ContractTypeField
                value={formData.contractType}
                onChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    contractType: value
                  }));
                }}
                disabled={isViewMode}
              />
              <EngagementModelField
                value={formData.engagementModel}
                onChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    engagementModel: value
                  }));
                }}
                disabled={isViewMode}
              />
            </>
          )}
        </FormRow>

        {/* ADR-138 — on rehire, the contract start date above is one thing and the
            employee record's hire date is another. Before this they were the same
            input, which is why "which episode does the hire date come from" could not
            be expressed at all. */}
        {isRehireMode && !isViewMode && !isSecondaryReplace && (
          <Box sx={{
            mt: 1,
            mb: 2,
            p: 2,
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            backgroundColor: '#f8fafc'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mb: 0.5 }}>
              Hire date on the employee record
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: '#1e293b' }}>
                {resolvedHireDateSourceDate ? formatEpisodeDate(resolvedHireDateSourceDate) : 'Not set'}
                <Typography component="span" variant="body2" sx={{ color: '#64748B' }}>
                  {hireDateSource.kind === HIRE_DATE_SOURCE_KIND.KEEP && ' — unchanged'}
                  {hireDateSource.kind === HIRE_DATE_SOURCE_KIND.NEW && ' — this rehire\'s start date'}
                  {hireDateSource.kind === HIRE_DATE_SOURCE_KIND.EPISODE && ' — from an earlier employment episode'}
                </Typography>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setEpisodePickerOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Change
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 1 }}>
              {originalHireDate
                ? `Currently on the record: ${formatEpisodeDate(originalHireDate)}.`
                : 'This person has no hire date on record.'}
            </Typography>
          </Box>
        )}

        {/* ADR-138 — the standalone action. Narrower than the free-text field above:
            it can only ever adopt an episode's start date, never invent one. */}
        {id && !isRehireMode && !isViewMode && canSetHireDateFromEpisode && (
          <Box sx={{ mt: -1, mb: 2 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setEpisodePickerOpen(true)}
              sx={{ textTransform: 'none', pl: 0 }}
            >
              Take hire date from an employment episode
            </Button>
          </Box>
        )}

        {!isSecondaryReplace && (
        <FormRow>
          <ClusterSelector
            value={formData.cluster}
            onChange={(clusterId) => {
              // ADR-142 §8 — the company is NOT reset on cluster change any
              // more: a cross-cluster company is legitimate (ajar picker);
              // the CompanySelector warns instead.
              setFormData(prev => ({
                ...prev,
                cluster: clusterId
              }));
            }}
            label="Region"
            placeholder="Select a region"
            required
            multiple={false}
            /* ADR-119 amendment 1: an individual is hired into a Region, so the
               picker offers the same set as the Individuals Repository "Regions"
               filter — Clusters of the Region structure type, flat. */
            structureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
            outOfScopeSuffix="not a Region"
            helperText="Individuals are hired into a Region"
            disabled={isViewMode}
          />
          <CountrySelector
            value={formData.workingLocation}
            onChange={(value) => {
              setFormData(prev => ({
                ...prev,
                workingLocation: value
              }));
            }}
            label="Working Location"
            required
            disabled={isViewMode}
          />
        </FormRow>
        )}
        {!showTermSelectors && (
          /* The read-only replacement for the term selectors on an existing
             person: the current contract at a glance, and the way to the
             surface that owns it. Values are §5.5-sourced from the employee
             payload — the same source the Contracts tab reads. */
          <Box sx={{
            mt: 1,
            mb: 2,
            p: 2,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {contractCardText(employeeData?.contractSummary, employeeData?.contracts).heading}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#1E293B', mt: 0.5 }}>
                {[
                  employeeData?.contractType?.name,
                  employeeData?.contractCompany?.name,
                  employeeData?.engagementModel?.name
                ].filter(Boolean).join(' · ') || 'No contract terms recorded'}
              </Typography>
              {/* ADR-164 §6 S1/S2 — the dates of the SAME contract as the terms
                  (contractSummary): start from the contract; end = its end when
                  closed, else its scheduled end, else open-ended. Never from the
                  primary pointer, which a leaver no longer has. */}
              <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.25 }}>
                {contractCardText(employeeData?.contractSummary, employeeData?.contracts).startText}
                {' · '}
                {contractCardText(employeeData?.contractSummary, employeeData?.contracts).endText}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              sx={{ textTransform: 'none' }}
              onClick={() => setActiveTab(TABS.CONTRACTS)}
            >
              {contractCardText(employeeData?.contractSummary, employeeData?.contracts).linkText}
            </Button>
          </Box>
        )}

        {showTermSelectors && (
        <FormRow>
          {/* ADR-142 §8 — the company picker is AJAR: Region is the
              person's organizational home, and a contract may be with a
              company belonging to another cluster. The picker warns on a
              cross-cluster choice instead of filtering it out, and
              employee.cluster != company.cluster is a legitimate state. */}
          <ContractCompanyField
            value={formData.contractCompany}
            onChange={(value) => {
              setFormData(prev => ({
                ...prev,
                contractCompany: value
              }));
            }}
            warnClusterId={formData.cluster}
            disabled={isViewMode}
          />
          {/* ADR-136: contract number for the contract being started — captured on
              HIRE and on REHIRE, since each starts a new employment episode and
              the number is stamped onto that episode (ADR-049 sourceReference).
              Hidden when merely editing an existing person: the number then
              belongs to an episode that already exists, and is corrected on the
              Employment Episodes tab. */}
          {(!id || isRehireMode) && (
            <ContractNumberField
              value={formData.contractNumber}
              onChange={(value) => handleInputChange('contractNumber')({ target: { value } })}
              disabled={isViewMode}
            />
          )}
        </FormRow>
        )}

        {/* ADR-136 phase 4: optional fixed-term capture — on HIRE and REHIRE only,
            the two verbs that start a new contract. An end date extends the emitted
            batch with the production resign legs (today's proven FTC encoding:
            singletons + Valid RESIGNATION_SUBMITTED + Pending TERMINATION_FINALIZED);
            left empty, the batch is byte-for-byte the open-ended one. The comment is
            auto-generated (A2) and editable, and lands as BOTH the episode comment
            and the scheduled end's reason. Changing an EXISTING contract's end is
            the phase-5 verb — deliberately not offered on edit. */}
        {(!id || isRehireMode) && !isViewMode && (
          <FormRow>
            {/* ADR-173 D2 — the shared fixed-term block: generation, touched
                flag and validation live in FixedTermFields. */}
            <FixedTermFields
              startYmd={formData.hireDate || null}
              endYmd={formData.contractEndDate || null}
              onEndChange={handleContractEndDateChange}
              comment={formData.contractEndComment}
              onCommentChange={handleContractEndCommentChange}
              contractNumber={formData.contractNumber}
              startLabel={isRehireMode ? 'contract start date' : 'hire date'}
              // ADR-188 D4 — a secondary's successor ends on/before the primary
              maxEndYmd={isSecondaryReplace ? replaceBounds.primaryEndYmd : null}
              endRequired={Boolean(isSecondaryReplace && replaceBounds.primaryEndYmd)}
            />
          </FormRow>
        )}
      </FormSection>

      {!isRehireMode
        && (
            hasPendingResignation()
            || formData.organizationalStatus === ORGANIZATIONAL_STATUSES.RESIGNED
          )
        && (
        <FormSection>
          <SectionTitle>Resignation Details</SectionTitle>
          <FormRow>
            <DatePicker
              label="Resign Date"
              value={formData.resignDate ? dayjs.utc(formData.resignDate) : null}
              onChange={(newValue) => {
                // Only update if we have a valid date
                if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                  handleInputChange('resignDate')({
                    target: { value: newValue.format('YYYY-MM-DD') }
                  });
                } else if (newValue === null) {
                  handleInputChange('resignDate')({
                    target: { value: null }
                  });
                }
              }}
              disabled={isViewMode || hasPendingResignation()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  inputProps: {
                    placeholder: 'DD/MM/YYYY'
                  }
                }
              }}
            />
            <FormControl fullWidth disabled={isViewMode || hasPendingResignation()}>
              <InputLabel>Resign Reason</InputLabel>
              <Select
                value={formData.resignReason || ''}
                onChange={handleInputChange('resignReason')}
                label="Resign Reason"
              >
                {RESIGN_REASON_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormRow>
          {formData.resignReason === RESIGN_REASONS.OTHER && (
            <FormRow>
              <TextField
                fullWidth
                label="Specify Reason"
                value={formData.resignReasonDetails || ''}
                onChange={handleInputChange('resignReasonDetails')}
                multiline
                rows={4}
                placeholder="Please specify the resignation reason..."
                disabled={isViewMode || hasPendingResignation()}
              />
            </FormRow>
          )}
        </FormSection>
      )}

      <FormSection>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle>Position and Role Information</SectionTitle>
          {id && !isRehireMode && !isViewMode && (
            <HeaderButton
              onClick={() => {
                const stateToPass = scrollState || location.state?.originalScrollState || location.state?.scrollState;
                navigate(`/employees/${id}/positions`, {
                  state: {
                    organizationalStatus: formData.organizationalStatus,
                    originalScrollState: stateToPass
                  }
                });
              }}
              sx={{
                padding: '6px 12px',
                fontSize: '14px',
                height: '36px'
              }}
            >
              <FaHistory style={{ marginRight: '8px' }} />
              Edit Position & Grade
            </HeaderButton>
          )}
        </Box>
        {/* ADR-139 — on a plain EDIT the two selectors below stay locked: a mid-contract
            position or grade change is a historised fact whose effective date must be
            chosen, which is what /employees/:id/positions is for. Rehire and replace
            START a contract, so the successor's position and grade are theirs to set —
            and the dating question is already answered (the new leg starts with the new
            episode, employee.service.js rehire branch). Before this they were locked in
            those modes too while still being SUBMITTED, so a successor silently
            inherited the outgoing contract's position with no way to change it, and both
            history-surface buttons are hidden here — there was no path at all. */}
        {isSecondaryReplace && (
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 1 }}>
            Position and grade belong to the person and stay as they are — replacing a secondary contract does not change them (ADR-164).
          </Typography>
        )}
        {isRehireMode && !isViewMode && !isSecondaryReplace && (
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 1 }}>
            {`The position and grade of the ${isReplaceMode ? 'replacement' : 'new'} contract. Changing them opens a new position/grade history leg starting on the new contract's start date; organizational roles below carry over unchanged.`}
          </Typography>
        )}
        {!isSecondaryReplace && (
        <FormRow>
          <PositionSelector
            value={formData.position}
            onChange={(value) => {
              setFormData(prev => ({
                ...prev,
                position: value
              }));
            }}
            onPositionChange={handlePositionChange}
            onSelectedPositionChange={handleSelectedPositionChange}
            required
            disabled={(!!id && !isRehireMode) || isViewMode}
          />
          {selectedPosition && selectedPosition.requiresGrade && (
            <GradeSelector
              value={formData.grade}
              onChange={(value) => {
                setFormData(prev => ({
                  ...prev,
                  grade: value
                }));
              }}
              required={selectedPosition?.requiresGrade}
              disabled={(!!id && !isRehireMode) || isViewMode}
            />
          )}
        </FormRow>
        )}
        {id && (
          <>
            {!isRehireMode && !isViewMode && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <HeaderButton
                  onClick={() => {
                    const stateToPass = scrollState || location.state?.originalScrollState || location.state?.scrollState;
                    navigate(`/employees/${id}/roles`, {
                      state: {
                        organizationalStatus: formData.organizationalStatus,
                        originalScrollState: stateToPass
                      }
                    });
                  }}
                  sx={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    height: '36px'
                  }}
                >
                  <FaHistory style={{ marginRight: '8px' }} />
                  Edit Organizational Roles
                </HeaderButton>
              </Box>
            )}
            <FormRow>
              <FormControl fullWidth>
                <InputLabel>Organizational Roles</InputLabel>
                <Select
                  multiple
                  value={formData.roles.filter(roleId => roles.some(role => role.id === roleId))}
                  onChange={handleInputChange('roles')}
                  label="Organizational Roles"
                  disabled
                >
                  {roles.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {/* ADR-139 decision 2 — organizational roles are PERSON-level, not
                    contract-level: no contract-starting payload carries them (rehireSchema,
                    replaceContractSchema, scheduleReplacementSchema, the REHIRE handler all
                    omit them), so they survive a rehire or replacement untouched. Read-only
                    in every mode that renders them; changed on the Organizational Roles
                    history page. Deliberately NOT hidden in rehire/replace mode the way
                    /employees/new omits them — the carry-over is a true and useful fact. */}
                <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5 }}>
                  {isRehireMode
                    ? 'Roles carry over to the new contract unchanged — change them on the Organizational Roles page.'
                    : 'Changed on the Organizational Roles page.'}
                </Typography>
              </FormControl>
            </FormRow>
          </>
        )}
      </FormSection>

      {id && (
        <FormSection>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <SectionTitle>Management Information</SectionTitle>
            {canViewManagementHistory && (
              <HeaderButton
                onClick={() => setManagementHistoryOpen(true)}
                sx={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  height: '36px'
                }}
              >
                <FaHistory style={{ marginRight: '8px' }} />
                History
              </HeaderButton>
            )}
          </Box>
          <FormRow>
            <TextField
              fullWidth
              label="Direct Representative"
              value={formData.directManager?.fullName || ''}
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmployeeAvatar
                        employee={{
                          fullName: formData.directManager?.fullName,
                          azureId: formData.directManager?.azureId,
                        }}
                        size={32}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              disabled
            />
            <TextField
              fullWidth
              label="Budget Director"
              value={formData.budgetDirector?.fullName || ''}
              slotProps={{
                input: {
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmployeeAvatar
                        employee={{
                          fullName: formData.budgetDirector?.fullName,
                          azureId: formData.budgetDirector?.azureId,
                        }}
                        size={32}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              disabled
            />
            {/* ADR-127: My Exec — representative of the cluster the FT allocation sits in */}
            <Tooltip title="Representative of the cluster your full-time project belongs to" placement="top-start">
              <TextField
                fullWidth
                label="My Exec"
                value={formData.myExec?.fullName || ''}
                slotProps={{
                  input: {
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmployeeAvatar
                          employee={{
                            fullName: formData.myExec?.fullName,
                            azureId: formData.myExec?.azureId,
                          }}
                          size={32}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
                disabled
              />
            </Tooltip>
          </FormRow>
          {canViewManagementHistory && (
            <ManagementHistoryDialog
              open={managementHistoryOpen}
              onClose={() => setManagementHistoryOpen(false)}
              employeeId={id}
              employeeName={formData.fullName || ''}
            />
          )}
        </FormSection>
      )}
    </FormContainer>
  );

  const renderProjects = () => (
    <FormContainer>
      <FormSection>
        {formData.projects.length === 0 ? (
          <EmptyState
            title="No Projects Found"
            description="This individual is not assigned to any projects yet."
          />
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{formData.projects.length}</strong> {formData.projects.length === 1 ? 'project' : 'projects'} found
              </Typography>
            </Box>
            <Grid 
              container 
              spacing={3}
              sx={{
                width: '100%',
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(3, 1fr)'
                },
                gap: '24px',
                '& .MuiGrid-item': {
                  padding: '0',
                  width: '100%'
                }
              }}
            >
              {formData.projects.map(project => (
                <Grid item key={project.id}>
                  <ProjectCard project={project} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </FormSection>
    </FormContainer>
  );

  const renderCompetencies = () => (
    <Competencies employeeData={formData} isViewMode={isViewMode} />
  );

  const renderContent = () => {
    switch (activeTab) {
      case TABS.EMPLOYMENT_INFORMATION:
        return renderEmploymentInformation();
      case TABS.PROJECTS:
        return renderProjects();
      case TABS.RESPONSIBILITIES:
        return <Responsibilities employeeId={id} />;
      case TABS.COMPETENCIES:
        return renderCompetencies();
      case TABS.ASSIGNMENTS:
        return canViewAssignments() ? <Assignments employeeId={id} /> : (
          <EmptyState
            title="Access Denied"
            description="You don't have permission to view assignments for this individual."
          />
        );
      case TABS.INSTANT_FEEDBACK:
        return canViewInstantFeedback() ? <InstantFeedback employeeId={id} /> : (
          <EmptyState
            title="Access Denied"
            description="You don't have permission to view instant feedback for this individual."
          />
        );
      case TABS.CONTRACTS:
        return <ContractDetails employeeId={id} />;
      case TABS.EMPLOYMENT_EPISODES:
        return canViewEmploymentEpisodes() ? <EmploymentEpisodes employeeId={id} primaryEmploymentEpisodeId={employeeData?.primaryEmploymentEpisodeId ?? employeeData?.currentEmploymentEpisodeId ?? null} /> : (
          <EmptyState
            title="Access Denied"
            description="You don't have permission to view employment episodes for this individual."
          />
        );
      case TABS.PLACEMENT_ACTIVITY:
        // ADR-009: per-activity authorization is driven entirely by the API's
        // `permissions: { canEdit, canDelete }` projection — no client-side `readOnly`
        // flag. The component renders affordances on a per-card basis, so a cross-BU
        // director viewing a bench individual sees Edit/Delete on activities they own
        // and "Read only" on the rest, while the home-BU manager sees full control.
        return canViewPlacementActivity() ? (
          <PlacementActivity employeeId={id} />
        ) : (
          <EmptyState
            title="Access Denied"
            description="You don't have permission to view placement activities for this individual."
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => {
              // If the caller passed `returnTo` on location.state, honour it and forward
              // the rest of the state so the destination can restore (e.g., BenchReport
              // reads `benchState` to restore filters and scroll). Falls back to the
              // existing onBack / individuals list behaviour.
              // ADR-113 addendum: returnTo/returnLabel are CONSUMED here — the
              // Contracts tab launches Replace with returnTo pointing back at
              // itself, and forwarding the pair would hijack the destination's
              // own Back button into a self-loop.
              const returnTo = location.state?.returnTo;
              if (returnTo) {
                const { returnTo: _consumedTo, returnLabel: _consumedLabel, replaceEpisodeId: _consumedEpisode, ...restState } = location.state || {};
                navigate(returnTo, { state: restState });
              } else if (onBack) {
                onBack();
              } else {
                navigate('/employees');
              }
            }}>
              <FaArrowLeft /> {location.state?.returnLabel
                ? `Back to ${location.state.returnLabel}`
                : 'Back to Individuals'}
            </BackButton>
            <Title>
              {id ? (
                isReplaceMode ? `Replace Contract${replaceTargetLabel ? ` · ${replaceTargetLabel}` : ''} | ${formData.fullName}` :
                isRehireMode ? `Rehire Individual | ${formData.fullName}` :
                isViewMode ? `View Individual | ${formData.fullName}` :
                `Edit Individual | ${formData.fullName}`
              ) : 'New Individual'}
            </Title>
          </HeaderLeft>

          {/* Header is `justify-content: space-between`, so trailing children
              get spread across the bar rather than grouped. The right-hand
              actions share one flex box: without it, Resign floated in the
              middle of the header with nothing to attach it to. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isRehireMode && activeTab === TABS.EMPLOYMENT_INFORMATION && (
              <HeaderButton onClick={isReplaceMode ? handleReplace : handleRehire}>
                {isReplaceMode
                  ? (isScheduledReplacement ? 'Schedule Replacement' : 'Replace Contract')
                  : 'ReHire'}
              </HeaderButton>
            )}

            {/* ADR-149 §5.2 — the person action area. Resign ends every
                contract the person holds, so it is a person verb and cannot
                sit on a contract card (§1, reversing ADR-113 §4's contract
                half). ADR-158 §7.2 adds Hire Withdraw and Mark No Show for
                the same reason: they void the PERSON's joining — every
                contract that has not started — and the person becomes
                Cancelled. ADR-159 §5.2 reads the three as one verb, End
                Employment, under three state-dependent labels.
                Scoped to the Employment Information tab (owner's call,
                2026-08-28; recorded as the rule by ADR-158 §7.2, correcting
                ADR-149 §5.2/§9.1's "every tab"): the person's verbs live on
                the person's tab, beside Update Individual. From the Contracts
                tab the route is this tab, or the repository card's menu.
                Gates are the person's — the primary's facts (ADR-156 §2) —
                unchanged from the card's entries: Resign while Employed with
                no pending resignation; Hire Withdraw while Joining; Mark No
                Show inside the backend-computed no-show window (ADR-136
                phase 3 §2a #6); all three manageable by this user. */}
            {id && !isRehireMode && !isViewMode
              && activeTab === TABS.EMPLOYMENT_INFORMATION
              && employeeData
              && canManageEmployee(employeeData)
              && personVerbsLockedBy(employeeData, viewerIsAdmin)
              // only where one of the three would otherwise be offered
              && (formData.organizationalStatus === ORGANIZATIONAL_STATUSES.JOINING
                || employeeData.canMarkNoShow
                || (formData.organizationalStatus === ORGANIZATIONAL_STATUSES.EMPLOYED
                  && !employeeData.hasPendingResignation)) && (
              // ADR-183 D2 — a person verb ends EVERY contract, so one
              // sync-owned contract locks all three for non-admins
              <Typography sx={{ fontSize: 12, color: '#64748B', maxWidth: 420 }}>
                {sourceLockNote(personVerbsLockedBy(employeeData, viewerIsAdmin).source, { person: true })}
              </Typography>
            )}
            {id && !isRehireMode && !isViewMode
              && activeTab === TABS.EMPLOYMENT_INFORMATION
              && employeeData
              && canManageEmployee(employeeData)
              && !personVerbsLockedBy(employeeData, viewerIsAdmin) && (
              <>
                {formData.organizationalStatus === ORGANIZATIONAL_STATUSES.JOINING && (
                  <Button
                    variant="outlined"
                    onClick={() => setPersonAction(PERSON_ACTIONS.HIRE_WITHDRAW)}
                    sx={PERSON_DESTRUCTIVE_SX}
                  >
                    Hire Withdraw
                  </Button>
                )}
                {employeeData.canMarkNoShow && (
                  <Button
                    variant="outlined"
                    onClick={() => setPersonAction(PERSON_ACTIONS.MARK_NO_SHOW)}
                    sx={PERSON_DESTRUCTIVE_SX}
                  >
                    Mark No Show
                  </Button>
                )}
                {formData.organizationalStatus === ORGANIZATIONAL_STATUSES.EMPLOYED
                  && !employeeData.hasPendingResignation && (
                  <Button
                    variant="outlined"
                    onClick={() => setPersonAction(PERSON_ACTIONS.RESIGN)}
                    sx={PERSON_DESTRUCTIVE_SX}
                  >
                    Resign
                  </Button>
                )}
              </>
            )}

            {!isRehireMode
              && !isViewMode
              && (!id
                  || (
                      formData.organizationalStatus !== ORGANIZATIONAL_STATUSES.RESIGNED
                      && formData.organizationalStatus !== ORGANIZATIONAL_STATUSES.CANCELLED
                    )
                  )
                  && activeTab === TABS.EMPLOYMENT_INFORMATION && (
              <HeaderButton onClick={handleSubmit}>
                {id ? 'Update Individual' : 'Create Individual'}
              </HeaderButton>
            )}
          </Box>
        </Header>

        {/* ADR-149 §5.3 — the same ContractActionModals the Contracts tab
            mounts, with `episode` deliberately null: a person verb names no
            contract, and its title carries no contract identity. */}
        <ContractActionModals
          action={personAction}
          employee={employeeData}
          episode={null}
          onClose={() => setPersonAction(null)}
          onCompleted={async () => { setPersonAction(null); await fetchEmployee(); }}
        />

        {/* ADR-113 addendum (2026-08-27) — replace/rehire are FOCUSED flows:
            the operator is composing a successor contract, and the other tabs
            are read surfaces of the CURRENT employment — noise mid-flow, and
            navigating them abandons nothing visibly. The tab bar hides
            entirely (the title already names the flow); the form renders the
            Employment Information content alone. The full tab bar belongs to
            ordinary edit/view. */}
        {!isRehireMode && (
        <TabContainer>
          <TabButton
            data-active={activeTab === TABS.EMPLOYMENT_INFORMATION}
            onClick={() => setActiveTab(TABS.EMPLOYMENT_INFORMATION)}
          >
            Employment Information
          </TabButton>
          {id && (
            <>
              <TabButton
                data-active={activeTab === TABS.PROJECTS}
                onClick={() => setActiveTab(TABS.PROJECTS)}
              >
                Projects
              </TabButton>
              <TabButton
                data-active={activeTab === TABS.RESPONSIBILITIES}
                onClick={() => setActiveTab(TABS.RESPONSIBILITIES)}
              >
                Responsibilities
              </TabButton>
              <TabButton
                data-active={activeTab === TABS.COMPETENCIES}
                onClick={() => setActiveTab(TABS.COMPETENCIES)}
              >
                Technology Radar
              </TabButton>
              {canViewAssignments() && (
                <TabButton
                  data-active={activeTab === TABS.ASSIGNMENTS}
                  onClick={() => setActiveTab(TABS.ASSIGNMENTS)}
                >
                Assignments
                </TabButton>
              )}
              {canViewInstantFeedback() && (
                <TabButton
                  data-active={activeTab === TABS.INSTANT_FEEDBACK}
                  onClick={() => setActiveTab(TABS.INSTANT_FEEDBACK)}
                >
                  Instant Feedback
                </TabButton>
              )}
              <TabButton
                data-active={activeTab === TABS.CONTRACTS}
                onClick={() => setActiveTab(TABS.CONTRACTS)}
              >
                Contracts
              </TabButton>
              {canViewEmploymentEpisodes() && (
                <TabButton
                  data-active={activeTab === TABS.EMPLOYMENT_EPISODES}
                  onClick={() => setActiveTab(TABS.EMPLOYMENT_EPISODES)}
                >
                  Employment Episodes
                </TabButton>
              )}
              {canViewPlacementActivity() && (
                <TabButton
                  data-active={activeTab === TABS.PLACEMENT_ACTIVITY}
                  onClick={() => setActiveTab(TABS.PLACEMENT_ACTIVITY)}
                >
                  Placement Activity
                </TabButton>
              )}
            </>
          )}
        </TabContainer>
        )}

        <Content>
          {renderContent()}
        </Content>

        {/* Azure AD Search Modal */}
        <Dialog 
          open={searchModalOpen} 
          onClose={() => {
            setSearchModalOpen(false);
            setSearchQuery('');
            setSearchResults([]);
            setIsSearching(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Search Azure AD Users</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              autoCorrect="off"
              spellCheck="false"
              autoComplete="new-password"
              name="search-azure-users"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch />
                    </InputAdornment>
                  ),
                  endAdornment: isSearching && (
                    <InputAdornment position="end">
                      <CircularProgress size={24} color="primary" />
                    </InputAdornment>
                  )
                }
              }}
              sx={{ mb: 2 }}
            />
            {isSearching && searchQuery && searchQuery.length >= 3 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  Searching for users ...
                </Typography>
              </Box>
            )}
            <List>
              {searchResults.map((user) => (
                <ListItem 
                  key={user.id} 
                  button 
                  onClick={() => handleUserSelect(user)}
                  sx={{
                    borderRadius: '4px',
                    mb: 1,
                    padding: '8px 16px',
                    '&:hover': {
                      backgroundColor: '#F1F5F9'
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 64 }}>
                    <EmployeeAvatar employee={{ azureId: user.id }} size={48} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={user.displayName}
                    secondary={user.upn}
                  />
                </ListItem>
              ))}
              {!isSearching && debouncedSearchQuery && debouncedSearchQuery.length >= 3 && searchResults.length === 0 && (
                <ListItem>
                  <ListItemText primary="No users found" />
                </ListItem>
              )}
              {!isSearching && searchQuery && searchQuery.length < 3 && (
                <ListItem>
                  <ListItemText primary="Please enter at least 3 characters to search" />
                </ListItem>
              )}
            </List>
          </DialogContent>
        </Dialog>

        {/* ADR-138 — mounted for both surfaces; which one is open decides what the
            confirmation does. Rendered only when the employee exists, since the
            episode list is fetched by id. */}
        {id && (
          <EpisodePicker
            open={episodePickerOpen}
            onClose={() => setEpisodePickerOpen(false)}
            onConfirm={handleHireDateSourceConfirm}
            employeeId={id}
            currentHireDate={originalHireDate}
            currentTenure={formData.timeInOrganization}
            newContractStartDate={isRehireMode ? rehireContractStartDate : null}
            allowKeepCurrent={isRehireMode}
            initialSelection={isRehireMode ? hireDateSource : null}
            title={
              isRehireMode
                ? 'Hire date source for this rehire'
                : 'Take the hire date from an employment episode'
            }
            confirmText={isRehireMode ? 'Use this' : 'Apply'}
            isSubmitting={isApplyingHireDate}
          />
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default EmployeeForm; 